import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  type StudentPerformance,
  useGetClassPerformanceAnalytics,
} from '~entities/classes/api/class-performance-analytics';

import { EnrollioTable } from '~shared/components/enrollio-table';
import {
  MedicalDetailsCard,
  StudentNameWithMedicalBadge,
} from '~shared/components/medical-details';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Progress } from '~shared/shadcn/ui/progress';

const columnHelper = createColumnHelper<StudentPerformance>();

const columns = [
  columnHelper.accessor('name', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Name'
        />
      );
    },
    cell: (info) => (
      <StudentNameWithMedicalBadge
        name={info.getValue()}
        medical={info.row.original.medical}
      />
    ),
  }),
  columnHelper.accessor('classesDone', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Classes Marked'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('classesAttended', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Classes Attended'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('attendancePercentage', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Attendance Percentage'
        />
      );
    },
    cell: (info) => {
      return <div>{info.getValue()}</div>;
    },
  }),
  columnHelper.accessor('status', {
    header: (info) => {
      return (
        <DataTableColumnHeader
          disableSorting
          column={info.column}
          title='Status'
        />
      );
    },
    cell: (info) => {
      const value = info.getValue();
      return (
        <div>
          <Badge
            className={cn(
              value === 'Excellent' && 'bg-green-500 text-white',
              value === 'Good' && 'bg-blue-500 text-white',
              value === 'Average' && 'bg-yellow-500 text-white',
              value === 'Needs Improvement' && 'bg-red-500 text-white',
            )}
          >
            {value}
          </Badge>
        </div>
      );
    },
  }),
];

export interface StudentsTabProps {
  classId: string;
}

export const StudentsTab = ({ classId }: StudentsTabProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isLoading } = useGetClassPerformanceAnalytics({
    enrollmentId: classId,
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
  });

  const tableData = useMemo(
    () => data?.studentPerformance ?? [],
    [data?.studentPerformance],
  );

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <Loader2 />
      </div>
    );
  }

  if (!data) {
    return <div>No Data</div>;
  }

  const studentPerformanceDistribution = data.studentDistribution;
  const totalNumberOfStudents = data.pagination.totalItems;
  const distributionPercent = (count: number) =>
    totalNumberOfStudents > 0
      ? Math.floor((count / totalNumberOfStudents) * 100)
      : 0;

  return (
    <div className='space-y-4'>
      <div className='gap-4 space-y-4 md:grid md:grid-cols-7 md:space-y-0'>
        <Card className='md:col-span-4'>
          <CardHeader className='p-2 sm:p-6'>
            <CardTitle>Student Performance</CardTitle>
            <CardDescription>
              Attendance and progress metrics for all students
            </CardDescription>
          </CardHeader>
          <CardContent className='p-2 pt-0 sm:p-6 sm:pt-0'>
            <EnrollioTable
              ariaLabel='Student performance'
              totalLabel='Students'
              columns={columns}
              data={tableData}
              rowCount={totalNumberOfStudents}
              manualPagination
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          </CardContent>
        </Card>
        <Card className='md:col-span-3'>
          <CardHeader className='p-2 sm:p-6'>
            <CardTitle>Student Distribution</CardTitle>
            <CardDescription>By performance level</CardDescription>
          </CardHeader>
          <CardContent className='p-2 pt-0 sm:p-6 sm:pt-0'>
            <div className='space-y-4'>
              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <div className='flex items-center'>
                    <div className='mr-2 size-3 rounded-full bg-green-500'></div>
                    <span>Excellent (90-100%)</span>
                  </div>
                  <span className='font-medium'>
                    {studentPerformanceDistribution['Excellent (90-100%)']}{' '}
                    students
                  </span>
                </div>
                <Progress
                  value={distributionPercent(
                    studentPerformanceDistribution['Excellent (90-100%)'],
                  )}
                  className='h-2'
                />
              </div>
              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <div className='flex items-center'>
                    <div className='mr-2 size-3 rounded-full bg-blue-500'></div>
                    <span>Good (75-89%)</span>
                  </div>
                  <span className='font-medium'>
                    {studentPerformanceDistribution['Good (75-89%)']} students
                  </span>
                </div>
                <Progress
                  value={distributionPercent(
                    studentPerformanceDistribution['Good (75-89%)'],
                  )}
                  className='h-2 bg-muted'
                />
              </div>
              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <div className='flex items-center'>
                    <div className='mr-2 size-3 rounded-full bg-yellow-500'></div>
                    <span>Average (60-74%)</span>
                  </div>
                  <span className='font-medium'>
                    {studentPerformanceDistribution['Average (60-74%)']}{' '}
                    students
                  </span>
                </div>
                <Progress
                  value={distributionPercent(
                    studentPerformanceDistribution['Average (60-74%)'],
                  )}
                  className='h-2 bg-muted'
                />
              </div>
              <div className='space-y-2'>
                <div className='flex items-center justify-between text-sm'>
                  <div className='flex items-center'>
                    <div className='mr-2 size-3 rounded-full bg-red-500'></div>
                    <span>Needs Improvement (&lt;60%)</span>
                  </div>
                  <span className='font-medium'>
                    {studentPerformanceDistribution['Needs Improvement (<60%)']}{' '}
                    students
                  </span>
                </div>
                <Progress
                  value={distributionPercent(
                    studentPerformanceDistribution['Needs Improvement (<60%)'],
                  )}
                  className='h-2 bg-muted'
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <MedicalDetailsCard classId={classId} />
    </div>
  );
};
